import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { signUp, confirmSignUp, signIn } from 'aws-amplify/auth';
import Header from './home/Header';
import AuthHero from './auth/AuthHero';
import Canonical from './Canonical';

function getFlagEmoji(countryCode) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

function Waitlist() {
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const [step, setStep] = useState(searchParams.get('success') ? 'success' : 'signup');
  const [formData, setFormData] = useState({
    businessName: '',
    businessWebsite: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    jobRole: '',
    referralSource: '',
    businessType: '',
    industry: '',
    servicesOffered: '',
    businessHours: '',
    monthlyCallVolume: ''
  });
  const [countryCode, setCountryCode] = useState('+1');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const getHiddenPassword = (email) => {
    const salt = process.env.REACT_APP_PASSWORD_SALT || 'default-salt-change-in-production';
    return btoa(`${email}:${salt}:rocket-lab-waitlist`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Apply masking for phone number if country code is +1
    if (name === 'phone' && countryCode === '+1') {
      const digits = value.replace(/\D/g, '');
      let formatted = digits;
      
      if (digits.length >= 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      } else if (digits.length >= 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      
      setFormData({ ...formData, [name]: formatted });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    const fullPhone = countryCode + formData.phone.replace(/\D/g, '');
    const phoneRegex = /^\+[1-9]\d{10,14}$/;
    if (!phoneRegex.test(fullPhone)) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }
    
    // Enhanced business validation
    if (!formData.businessName.trim() || formData.businessName.length < 2) {
      setError('Business name must be at least 2 characters');
      setLoading(false);
      return;
    }
    
    if (!formData.servicesOffered.trim()) {
      setError('Please describe the services your business offers');
      setLoading(false);
      return;
    }
    
    if (!formData.businessHours.trim()) {
      setError('Please provide your business hours');
      setLoading(false);
      return;
    }
    
    // Optional website domain validation (only if website is provided)
    if (formData.businessWebsite && formData.businessWebsite.trim()) {
      const websiteDomain = formData.businessWebsite.replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
      const emailDomain = formData.email.split('@')[1];
      if (emailDomain !== websiteDomain) {
        setError('Business email domain should match business website (or leave website blank)');
        setLoading(false);
        return;
      }
    }
    
    try {
      const hiddenPassword = getHiddenPassword(formData.email);
      
      await signUp({
        username: formData.email,
        password: hiddenPassword,
        options: {
          userAttributes: {
            email: formData.email,
            name: `${formData.firstName} ${formData.lastName}`,
            phone_number: fullPhone,
            'custom:business_type': formData.businessType,
            'custom:industry': formData.industry,
            'custom:call_volume': formData.monthlyCallVolume,
            'custom:user_type': 'waitlist'
          }
        }
      });
      
      setStep('confirm');
    } catch (err) {
      if (err.name === 'UsernameExistsException') {
        setStep('confirm');
        setError('Account exists. Please enter your confirmation code.');
      } else {
        setError(err.message || 'Failed to sign up');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { resendSignUpCode } = await import('aws-amplify/auth');
      await resendSignUpCode({ username: formData.email });
      setError('✅ Verification code resent to ' + formData.email);
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await confirmSignUp({
        username: formData.email,
        confirmationCode: confirmationCode
      });
      
      const hiddenPassword = getHiddenPassword(formData.email);
      await signIn({
        username: formData.email,
        password: hiddenPassword
      });
      
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
      const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
      const idToken = localStorage.getItem(idTokenKey);
      
      const fullPhone = countryCode + formData.phone.replace(/\D/g, '');
      const response = await fetch('https://oauth.remote-assistant.ai/oauth/waitlist', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessName: formData.businessName,
          businessWebsite: formData.businessWebsite,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: fullPhone,
          jobRole: formData.jobRole,
          referralSource: formData.referralSource,
          businessType: formData.businessType,
          industry: formData.industry,
          servicesOffered: formData.servicesOffered,
          businessHours: formData.businessHours,
          monthlyCallVolume: formData.monthlyCallVolume
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit waitlist');
      }
      
      // Clear all auth data for waitlist users to prevent refresh token storage
      localStorage.clear();
      sessionStorage.clear();
      
      window.location.href = `/waitlist?success=true&email=${encodeURIComponent(formData.email)}`;
    } catch (err) {
      setError(err.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'confirm') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex flex-1">
          <AuthHero />
          <div className="w-full lg:w-2/3 flex justify-center items-center px-8 py-12 bg-[#f9f9fb]">
            <div className="bg-white p-10 rounded-xl border border-gray-200 w-full max-w-xl">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-['Inter']">Verify Your Email</h2>
              <p className="text-gray-600 mb-6 text-sm font-['Inter']">
                We sent a verification code to <strong>{formData.email}</strong>
              </p>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm border border-red-200">
                  {error}
                </div>
              )}

              <form onSubmit={handleConfirm} className="flex flex-col gap-3">
                <input 
                  type="text" 
                  placeholder="Enter verification code" 
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  required 
                  className="p-3 border border-gray-300 rounded-lg text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button 
                  type="submit" 
                  disabled={loading}
                  className={`btn-primary text-sm py-3 ${
                    loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  {loading ? 'Verifying...' : 'Verify & Join Waitlist'}
                </button>
                <button 
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  className="text-blue-600 hover:text-blue-700 text-xs underline"
                >
                  Resend verification code
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    const email = searchParams.get('email') || formData.email;
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex flex-1">
          <AuthHero />
          <div className="w-full lg:w-2/3 flex justify-center items-center px-8 py-12 bg-[#f9f9fb]">
            <div className="bg-white p-10 rounded-xl border border-gray-200 w-full max-w-xl text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 font-['Inter']">You're on the list! 🎉</h2>
              <p className="text-gray-600 mb-6 text-sm">
                Your application is under review. We'll email you at <strong>{email}</strong> when you're approved.
              </p>
              <p className="text-xs text-gray-500 mb-6">
                Once approved, you'll receive an email to set your password and activate your account.
              </p>
              <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium no-underline text-sm">
                Return to home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Canonical path="/waitlist" />
      <Header />
      <div className="flex flex-1">
        <AuthHero />
        
        <div className="w-full lg:w-2/3 flex justify-center items-center px-8 py-12 bg-[#f9f9fb]">
          <div className="bg-white p-10 rounded-xl border border-gray-200 w-full max-w-xl">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-['Inter']">Request Access</h2>
            <p className="text-gray-600 mb-6 text-sm font-['Inter']">Join the waitlist. Onboarding weekly.</p>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm border border-red-200">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input type="text" name="businessName" placeholder="Business Name" value={formData.businessName} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <input type="url" name="businessWebsite" placeholder="Business Website (optional - e.g., example.com)" value={formData.businessWebsite} onChange={handleInputChange} className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <input type="email" name="email" placeholder="Business Email" value={formData.email} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <div className="flex gap-2 w-full">
                <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm w-1/2 min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm w-1/2 min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div className="flex gap-2">
                <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="p-3 border border-gray-300 rounded-lg text-sm w-24 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="+1">{getFlagEmoji('US')} +1</option>
                  <option value="+44">{getFlagEmoji('GB')} +44</option>
                  <option value="+33">{getFlagEmoji('FR')} +33</option>
                  <option value="+49">{getFlagEmoji('DE')} +49</option>
                  <option value="+39">{getFlagEmoji('IT')} +39</option>
                  <option value="+34">{getFlagEmoji('ES')} +34</option>
                  <option value="+61">{getFlagEmoji('AU')} +61</option>
                  <option value="+81">{getFlagEmoji('JP')} +81</option>
                </select>
                <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <input type="text" name="jobRole" placeholder="Job Role (e.g., Owner, Manager)" value={formData.jobRole} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <select name="referralSource" value={formData.referralSource} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">How did you find out about us?</option>
                <option value="search">Search Engine</option>
                <option value="social">Social Media</option>
                <option value="referral">Referral</option>
                <option value="ad">Advertisement</option>
                <option value="other">Other</option>
              </select>
              <select name="businessType" value={formData.businessType} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">What type of business?</option>
                <option value="auto">Auto Service</option>
                <option value="dental">Dental Practice</option>
                <option value="government">Government</option>
                <option value="home">Home Services</option>
                <option value="medical">Medical Practice</option>
                <option value="professional">Professional Services</option>                  
                <option value="personal-trainer">Personal Trainer</option>                
                <option value="restaurant">Restaurant / Food Service</option>
                <option value="retail">Retail / E-commerce</option>
                <option value="salon">Hair Salon / Barbershop</option>
                <option value="spa">Spa / Wellness</option>
                <option value="trades">Trades</option>
                <option value="other">Other</option>                
              </select>
              <select name="industry" value={formData.industry} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">What industry?</option>
                <option value="healthcare">Healthcare</option>
                <option value="beauty">Beauty & Personal Care</option>
                <option value="automotive">Automotive</option>
                <option value="hospitality">Hospitality & Tourism</option>
                <option value="retail">Retail</option>
                <option value="professional">Professional Services</option>
                <option value="technology">Technology</option>
                <option value="education">Education</option>
                <option value="real_estate">Real Estate</option>
                <option value="finance">Finance & Insurance</option>
                <option value="other">Other</option>
              </select>
              <textarea name="servicesOffered" placeholder="What services does your business offer? (e.g., haircuts, dental cleanings, car repairs)" value={formData.servicesOffered} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[80px] resize-none" />
              <input type="text" name="businessHours" placeholder="Business Hours (e.g., Mon-Fri 9AM-6PM, Sat 10AM-4PM)" value={formData.businessHours} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <select name="monthlyCallVolume" value={formData.monthlyCallVolume} onChange={handleInputChange} required className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">Monthly Call Volume</option>
                <option value="0-100">0-100 calls</option>
                <option value="100-500">100-500 calls</option>
                <option value="500-1000">500-1,000 calls</option>
                <option value="1000-5000">1,000-5,000 calls</option>
                <option value="5000+">5,000+ calls</option>
              </select>
              <button type="submit" disabled={loading} className={`btn-primary text-sm py-3 ${loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}>
                {loading ? 'Submitting...' : 'Request Access'}
              </button>
            </form>

            <p className="mt-4 text-xs text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium no-underline">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Waitlist;
